import React, { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { istDate } from "@/components/DispatchDatePicker";
import { DirToggle } from "./OpeningForms";
import { fmtD, inr } from "./PayoutBits";

const TYPES = [["opening", "Opening Balance"], ["payment", "Payment Made"], ["plus", "Bonus / Extra"], ["minus", "Deduction"], ["work", "Work (Qty × Rate)"]];
const box = "px-2 py-1.5 text-sm border border-[#DCDCD3] rounded bg-white min-w-0";
const blank = (kind) => ({ kind, amount: "", qty: "", rate: "", dir: "dena", date: istDate(0), note: "" });

function AmountCell({ f, set, setF }) {
  if (f.kind === "work") {
    return (
      <div className="flex gap-1 min-w-0">
        <input type="number" min="0" placeholder="Qty" value={f.qty} onChange={set("qty")} className={`${box} w-20 font-mono`} data-testid="quick-entry-qty" />
        <input type="number" min="0" step="0.01" placeholder="Rate" value={f.rate} onChange={set("rate")} className={`${box} w-20 font-mono`} data-testid="quick-entry-rate" />
      </div>
    );
  }
  return (
    <div className={`flex gap-1 items-center min-w-0 ${f.kind === "opening" ? "col-span-2 md:col-span-1" : ""}`}>
      <input type="number" min="0" step="0.01" placeholder="Amount" value={f.amount} onChange={set("amount")} className={`${box} w-28 shrink-0 font-mono`} data-testid="quick-entry-amount" />
      {f.kind === "opening" && <DirToggle compact value={f.dir} onChange={(dir) => setF({ ...f, dir })} testid="quick-entry-dir" />}
    </div>
  );
}

export function QuickEntry({ person, ob, onSaved }) {
  const kinds = TYPES.filter(([k]) => k !== "work" || person.kind === "booker");
  const [f, setF] = useState(blank("payment"));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const post = async () => {
    setBusy(true);
    try {
      if (f.kind === "opening") {
        if (f.amount === "") throw new Error("Enter an amount (0 is allowed)");
        await api.put(`/payouts/people/${person.id}/opening`, { date: f.date, amount: (f.dir === "lena" ? -1 : 1) * (+f.amount || 0), note: f.note });
      } else {
        await api.post(`/payouts/people/${person.id}/entries`, { kind: f.kind, date: f.date, qty: +f.qty || 0, rate: +f.rate || 0, amount: +f.amount || 0, mode: "", note: f.note });
      }
      toast.success(`${TYPES.find(([k]) => k === f.kind)[1]} posted`);
      setF(blank(f.kind));
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-[#EDEDE5] rounded p-3" data-testid="quick-entry">
      <div className="text-[10px] uppercase tracking-wider text-[#6B6B61] mb-2">Add manual entry</div>
      <div className="grid grid-cols-2 md:grid-cols-[180px_auto_150px_1fr_auto] gap-2 items-center">
        <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={`${box} ${f.kind === "opening" ? "col-span-2 md:col-span-1" : ""}`} data-testid="quick-entry-kind">
          {kinds.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <AmountCell f={f} set={set} setF={setF} />
        <input type="date" value={f.date} min={f.kind !== "opening" ? ob?.date : undefined} max={istDate(0)} onChange={set("date")} className={box} data-testid="quick-entry-date" />
        <input placeholder="Note (e.g. 'Cash ₹5000 paid on 03-Jul')" value={f.note} onChange={set("note")} className={box} data-testid="quick-entry-note" />
        <button onClick={post} disabled={busy} className="px-3 py-1.5 bg-[#385C4B] text-white rounded text-sm hover:bg-[#2D4A3C] disabled:opacity-50 flex items-center justify-center gap-1" data-testid="quick-entry-post">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}{busy ? "Posting…" : "Post"}
        </button>
      </div>
      {f.kind === "work" && <div className="text-xs mt-2" data-testid="quick-entry-work-amount">Amount: <b>{inr((+f.qty || 0) * (+f.rate || 0))}</b></div>}
      {f.kind === "opening" && (
        <div className="text-[11px] text-[#6B6B61] mt-2" data-testid="quick-entry-opening-hint">
          Entries before this date are not counted.{ob ? ` The current opening (${inr(ob.amount)} · from ${fmtD(ob.date)}) will be replaced.` : ""}
        </div>
      )}
    </div>
  );
}
